import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'

/**
 * CompletionStep - Minimal completion celebration
 *
 * "Design is not just what it looks like. Design is how it works." — Steve Jobs
 */

const ECOSYSTEM_DOMAINS = [
  { name: 'lukhas.ai', description: 'Intelligence' },
  { name: 'lukhas.dev', description: 'Build' },
  { name: 'lukhas.cloud', description: 'Deploy' },
  { name: 'lukhas.com', description: 'Enterprise' },
  { name: 'lukhas.store', description: 'Apps' },
  { name: 'lukhas.team', description: 'Collaborate' },
]

export default function CompletionStep() {
  const [showDomains, setShowDomains] = useState(false)
  const navigate = useNavigate()
  const { user, resetOnboarding } = useAuthStore()

  useEffect(() => {
    const timer = setTimeout(() => setShowDomains(true), 800)
    return () => clearTimeout(timer)
  }, [])

  const handleEnterDashboard = () => {
    resetOnboarding()
    navigate('/dashboard')
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-12"
    >
      {/* Success indicator - minimal */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        className="text-center"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 border border-white/20">
          <span className="text-4xl font-extralight text-white">Λ</span>
        </div>
      </motion.div>

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="space-y-3 text-center"
      >
        <h2 className="text-3xl font-extralight tracking-tight text-white">
          Welcome.
        </h2>
        <p className="text-white/40 font-light">
          Your identity is ready, {user?.displayName || user?.username || 'pioneer'}.
        </p>
      </motion.div>

      {/* User info - minimal card */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="p-6 border border-white/10"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 border border-white/20 flex items-center justify-center text-white font-light text-lg">
            {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-white font-light">
              {user?.displayName || user?.username || 'LUKHAS User'}
            </p>
            <p className="text-white/40 text-sm font-mono">
              {user?.email}
            </p>
          </div>
        </div>
      </motion.div>

      {/* One identity message */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center text-white/60 font-light tracking-wide"
      >
        One identity. Every domain.
      </motion.p>

      {/* Ecosystem domains - minimal pills */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showDomains ? 1 : 0 }}
        className="flex flex-wrap justify-center gap-3"
      >
        {ECOSYSTEM_DOMAINS.map((domain, index) => (
          <motion.a
            key={domain.name}
            href={`https://${domain.name}`}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0 }}
            animate={{ opacity: showDomains ? 1 : 0 }}
            transition={{ delay: index * 0.1 }}
            className="
              px-4 py-2 text-xs
              border border-white/10 hover:border-white/20
              text-white/50 hover:text-white/70
              transition-all duration-300
            "
          >
            {domain.name}
          </motion.a>
        ))}
      </motion.div>

      {/* Enter dashboard button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <button
          onClick={handleEnterDashboard}
          className="
            w-full py-4
            bg-white text-black text-sm font-medium tracking-wide
            hover:bg-white/90 active:bg-white/80
            transition-all duration-200
            flex items-center justify-center gap-3
          "
        >
          <span>Enter Dashboard</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Closing note */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="text-center text-[10px] text-white/20 font-light"
      >
        Your identity is your constellation anchor.
      </motion.p>
    </motion.div>
  )
}
